import { numberUtils } from '@pukka/common/utils';
import { Modal, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import RefundDiscount from './RefundDiscount';
import RefundGratuity from './RefundGratuity';
import { RefundItemList } from './RefundItemList';
import { RefundReason } from './RefundReason';
import RefundRewardRedemption from './RefundRewardRedemption';
import { RefundRounding } from './RefundRounding';
import RefundTax from './RefundTax';
import { calculateDiscount, calculateGratuity, calculateRedeemAmount, calculateTax, getRewardName } from './refundUtils';
import { handleIssueRefund, TransactionRefundState } from './TransactionRefund.state';

interface TransactionRefundModalProps {
  readonly state?: typeof TransactionRefundState;
  readonly onCloseDetail?: () => void;
}

function TransactionRefundModal({ state = TransactionRefundState, onCloseDetail }: TransactionRefundModalProps) {
  const { refundItems, refundItemsPromo, transactionDetail, handleCloseModal, useTransactionRefund } = state;
  const { isButtonEnabled, amountToRefund, isLoading } = useSnapshot(state);

  const paymentDiscounts = transactionDetail?.order_discounts.map((disc) => {
    return {
      ...disc,
      finalAmount: calculateDiscount(disc.discount_amount, transactionDetail, refundItems, refundItemsPromo),
    };
  });

  const paymentGratuities = transactionDetail?.order_gratuities.map((gratuity) => {
    return {
      ...gratuity,
      finalAmount: calculateGratuity(gratuity.gratuity_id, transactionDetail, refundItems),
    };
  });

  const paymentTaxes = transactionDetail?.order_taxes.map((tax) => {
    return {
      ...tax,
      finalAmount: calculateTax(tax.tax_id, transactionDetail, refundItems),
    };
  });

  useTransactionRefund();

  const handleClickIssueRefund = () => {
    handleIssueRefund(onCloseDetail);
  };

  return transactionDetail ? (
    <Modal size="lg" unwrap open>
      <Modal.Content>
        <Modal.Title>Issue Refund</Modal.Title>
        <Modal.Body className="min-h-[500px]">
          <div className="mb-4 flex justify-between rounded-md border p-4">
            <Text>Amount to refund</Text>
            <Text>{numberUtils.formatCurrency(amountToRefund, 'Rp.')}</Text>
          </div>
          {/* Items */}
          <RefundItemList />
          {/* Discount */}
          <RefundDiscount paymentDiscounts={paymentDiscounts} />
          {/* Reward Redemption */}
          <RefundRewardRedemption redeemReward={getRewardName(transactionDetail)} redeemAmount={calculateRedeemAmount(refundItems)} />
          {/* Gratuity */}
          <RefundGratuity paymentGratuities={paymentGratuities} includeGratuityTax={transactionDetail.include_gratuity_tax} />
          {/* Tax */}
          <RefundTax paymentTaxes={paymentTaxes} includeGratuityTax={transactionDetail.include_gratuity_tax} />
          {/* Rounding */}
          <RefundRounding refundItems={refundItems} transactionDetail={transactionDetail} />
          {/* Reason */}
          <RefundReason />
        </Modal.Body>
        <Modal.Footer
          onCancel={handleCloseModal}
          buttons={
            <Modal.Button onClick={handleClickIssueRefund} disabled={!isButtonEnabled} loading={isLoading}>
              Issue Refund
            </Modal.Button>
          }
        />
      </Modal.Content>
    </Modal>
  ) : null;
}

export { TransactionRefundModal };
